/**
 * Session-scoped memory for the Groups surfaces.
 *
 * The web app unmounts Home when it routes to a group or a profile, so the
 * active sub-tab and scroll offset have to survive outside React state for
 * "back" to land the member where they left off.
 */
import { GROUP_TABS } from '@wlb/shared/constants';
import type { GroupTabKey } from '@wlb/shared/constants';

const HOME_TAB_KEY = 'wlb:groups:tab';
const DETAIL_TAB_PREFIX = 'wlb:group:tab:';
const SCROLL_PREFIX = 'wlb:scroll:';

const GROUP_TAB_VALUES: string[] = Object.values(GROUP_TABS);

const readItem = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    // Private-mode browsers can throw on storage access; fall back to defaults.
    return null;
  }
};

const writeItem = (key: string, value: string): void => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Losing the restore point is preferable to breaking navigation.
  }
};

export const readGroupsTab = (): GroupTabKey => {
  const stored = readItem(HOME_TAB_KEY);
  return stored && GROUP_TAB_VALUES.includes(stored)
    ? (stored as GroupTabKey)
    : GROUP_TABS.POSTS;
};

export const writeGroupsTab = (tab: GroupTabKey): void => writeItem(HOME_TAB_KEY, tab);

export const readGroupDetailTab = (groupId: string | undefined, allowed: readonly string[]): string | null => {
  if (!groupId) return null;
  const stored = readItem(`${DETAIL_TAB_PREFIX}${groupId}`);
  return stored && allowed.includes(stored) ? stored : null;
};

export const writeGroupDetailTab = (groupId: string | undefined, tab: string): void => {
  if (!groupId) return;
  writeItem(`${DETAIL_TAB_PREFIX}${groupId}`, tab);
};

export const readScrollOffset = (key: string): number => {
  const stored = Number(readItem(`${SCROLL_PREFIX}${key}`));
  return Number.isFinite(stored) && stored > 0 ? stored : 0;
};

export const writeScrollOffset = (key: string, offset: number): void =>
  writeItem(`${SCROLL_PREFIX}${key}`, String(Math.max(0, Math.round(offset))));
